import format from 'date-fns/format';
import { Fragment, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import type { PartialSaveOptions } from 'payments/types/form';
import { useAccessToken } from 'reservations/account/providers/AccessTokenProvider';
import { useLocationId } from 'reservations/account/providers/ConfigProvider';
import { trackUpdateBooking } from 'reservations/analytics/helpers';
import BaseButton from 'reservations/base/components/BaseButton';
import { NEXTABLE_ERROR_CODE } from 'reservations/base/constants/nextable';
import { useErrorSnackbar } from 'reservations/base/hooks/useSnackbar';
import { useLoadingSpinner } from 'reservations/base/providers/LoadingSpinnerProvider';
import { useCustomRouter } from 'reservations/base/providers/RouterProvider';
import { useBooking } from 'reservations/booking/providers/BookingProvider';
import type {
  HandleErrorOptions,
  HandleUpdateBookingSuccessOptions
} from 'reservations/booking/types';
import { useIsSubjectToCancellationFee } from 'reservations/feePolicy/hooks';
import { useUpdateBookingMutation } from 'reservations/form/queries/useUpdateBookingMutation';
import { RequiredPayment } from 'reservations/payments/components/RequiredPayment';
import { usePaymentProviderSubmission } from 'reservations/payments/hooks/usePaymentProviderSubmission';

interface UpdateReservationButtonProps {
  handleFormDrawerClose: () => void;
}

const UpdateReservationButton = ({
  handleFormDrawerClose
}: UpdateReservationButtonProps) => {
  const partySize = useWatch({
    name: 'partySize'
  });
  const isSubjectToCancellationFee = useIsSubjectToCancellationFee(partySize);
  const ButtonWrapper = isSubjectToCancellationFee ? RequiredPayment : Fragment;

  return (
    <ButtonWrapper>
      <InternalUpdateReservationButton
        handleFormDrawerClose={handleFormDrawerClose}
        isSubjectToCancellationFee={isSubjectToCancellationFee}
      />
    </ButtonWrapper>
  );
};

interface InternalUpdateReservationButtonProps
  extends UpdateReservationButtonProps {
  isSubjectToCancellationFee: boolean;
}

const InternalUpdateReservationButton = ({
  handleFormDrawerClose,
  isSubjectToCancellationFee
}: InternalUpdateReservationButtonProps) => {
  const { handleHasLoaded, handleIsLoading } = useLoadingSpinner();
  const { handleNavigation } = useCustomRouter();
  const { handleSubmit } = useFormContext();
  const locationId = useLocationId();
  const { apiKey, slug } = useAccessToken();
  const enqueueErrorSnackbar = useErrorSnackbar();
  const {
    booking,
    checklessPayments,
    savedPaymentMethod: currentPaymentMethod
  } = useBooking();
  const { bookingId, dateAndTime } = booking;
  const { optInChecklessPayments, tipPercentage } = checklessPayments;
  const [availability, savedPaymentMethod] = useWatch({
    name: ['availability', 'savedPaymentMethod']
  });
  const previousReservation = useMemo(
    () => format(new Date(dateAndTime), "h:mmaa 'on' EEEE, LLL d"),
    [dateAndTime]
  );
  const onSuccess = () => {
    handleFormDrawerClose();
    handleHasLoaded();
  };

  const handlePaymentProviderSubmission =
    usePaymentProviderSubmission(onSuccess);

  // istanbul ignore next
  const handleSuccess = ({
    navUrl,
    booking
  }: HandleUpdateBookingSuccessOptions) => {
    // TODO: Ask Robert what implications there are if we try to update the payment method
    // when we're updating the reservation. Will it come back as pending again?

    const shouldSubmitPaymentProviderForm =
      isSubjectToCancellationFee &&
      savedPaymentMethod !== currentPaymentMethod?.id;

    if (shouldSubmitPaymentProviderForm) {
      const payload: PartialSaveOptions = {
        locationId,
        bookingId: booking.bookingId,
        email: booking.email,
        firstName: booking.firstName,
        lastName: booking.lastName,
        phone: booking.phone,
        phoneCode: booking.phoneCode,
        // TODO: Update once nextable is able to handle modifications
        // that change whether a booking is subject to cancellation or not
        shouldConfirmCardWithNextable: false,
        optInChecklessPayments,
        tipPercentage,
        ...(savedPaymentMethod && { savedPaymentMethod })
      };

      handlePaymentProviderSubmission(payload);
    } else {
      onSuccess();

      if (navUrl) {
        handleNavigation(navUrl);
      }
    }
  };

  // istanbul ignore next
  const handleError = ({
    navUrl,
    code,
    forceReload = false
  }: HandleErrorOptions) => {
    handleHasLoaded();

    if (
      code === NEXTABLE_ERROR_CODE.NO_AVAILABILITY ||
      NEXTABLE_ERROR_CODE.TIME_SLOT_UNAVAILABLE
    ) {
      enqueueErrorSnackbar(
        'UPDATE_BOOKING_ERROR_SNACKBAR',
        `${availability} is no longer available. You're still confirmed
            for ${previousReservation}`
      );
    }

    if (navUrl || forceReload) {
      handleNavigation(navUrl, forceReload);
    }
  };

  const mutation = useUpdateBookingMutation({
    slug,
    bookingId,
    apiKey,
    handleSuccess,
    handleError
  });

  const onSubmit = (data: Record<string, any>) => {
    handleIsLoading('Updating your reservation...');
    trackUpdateBooking(data);
    mutation.mutate(data);
  };

  return (
    <BaseButton label="Update Reservation" onClick={handleSubmit(onSubmit)} />
  );
};

export default UpdateReservationButton;
